<template>
  <div class="ai-audit-panel" v-loading="loading" element-loading-text="加载 AI 审核结果...">
    <!-- 折叠头部 -->
    <div class="ai-header" @click="expanded = !expanded">
      <div class="ai-header-left">
        <i class="el-icon-cpu ai-icon"></i>
        <span class="ai-title">AI 智能审核结果</span>

        <!-- 状态标签 -->
        <el-tag v-if="!task" size="small" type="info">未执行</el-tag>
        <el-tag v-else-if="task.status === 'PENDING' || task.status === 'RUNNING'"
                size="small" type="warning">
          <i class="el-icon-loading" style="margin-right:4px"></i>{{ task.status === 'PENDING' ? '排队中' : '审核中' }}
        </el-tag>
        <el-tag v-else-if="task.status === 'FAILED'" size="small" type="danger">AI 失败</el-tag>
        <el-tag v-else size="small" :type="overallTagType">{{ overallText }}</el-tag>

        <!-- 数字摘要 -->
        <span v-if="task && task.status === 'SUCCESS'" class="ai-summary">
          <span style="color:#67C23A">通过 {{ task.passCount || 0 }}</span> ·
          <span style="color:#F56C6C">失败 {{ task.failCount || 0 }}</span> ·
          <span style="color:#E6A23C">警告 {{ task.warnCount || 0 }}</span>
          <span style="color:#909399"> (共 {{ task.totalCount || 0 }} 项)</span>
        </span>
      </div>
      <div class="ai-header-right">
        <el-button v-if="task" size="mini" type="text" icon="el-icon-refresh"
                   @click.stop="reload">刷新</el-button>
        <i :class="['el-icon-arrow-down', 'ai-arrow', { rotated: expanded }]"></i>
      </div>
    </div>

    <!-- 展开内容 -->
    <transition name="el-collapse-transition">
      <div v-show="expanded" class="ai-body">
        <!-- 1. 未执行 -->
        <el-empty v-if="!task" description="该结算单尚未触发 AI 审核" :image-size="60">
          <div style="color:#909399;font-size:12px">
            AI 审核会在结算单提交时自动触发,异步执行
          </div>
        </el-empty>

        <!-- 2. RUNNING -->
        <div v-else-if="task.status === 'PENDING' || task.status === 'RUNNING'" class="running-tip">
          <i class="el-icon-loading" style="font-size:24px;color:#E6A23C"></i>
          <div style="margin-top:8px">
            AI 正在审核中(预计 30~60 秒),提交时间:{{ task.startTime || task.createTime }}
          </div>
          <div style="margin-top:4px;color:#909399;font-size:12px">
            正在自动轮询...
          </div>
        </div>

        <!-- 3. FAILED -->
        <div v-else-if="task.status === 'FAILED'" class="failed-tip">
          <el-alert type="warning" :closable="false" show-icon>
            <template slot="title">
              AI 审核执行失败,本次不阻断审核流程,请人工审核
            </template>
            <div style="margin-top:8px;color:#606266;font-size:12px;white-space:pre-wrap">
              {{ task.errorMsg || '未知错误' }}
            </div>
            <div style="margin-top:6px;color:#909399;font-size:12px">
              耗时: {{ formatCost(task.costMs) }}
            </div>
          </el-alert>
        </div>

        <!-- 4. SUCCESS -->
        <div v-else class="success-body">
          <!-- 元数据 -->
          <el-descriptions :column="4" border size="mini" class="meta">
            <el-descriptions-item label="任务 ID">{{ task.id }}</el-descriptions-item>
            <el-descriptions-item label="耗时">{{ formatCost(task.costMs) }}</el-descriptions-item>
            <el-descriptions-item label="Tokens">
              in {{ task.promptTokens }} / out {{ task.completionTokens }}
            </el-descriptions-item>
            <el-descriptions-item label="重试次数">
              <span :style="{ color: task.retryCount > 0 ? '#E6A23C' : '#67C23A' }">
                {{ task.retryCount || 0 }}
              </span>
            </el-descriptions-item>
            <el-descriptions-item label="使用模型" :span="2">
              <span style="font-family:monospace;font-size:12px">{{ llmInfo.model || '-' }}</span>
              <el-tag v-if="llmInfo.supportsVision === 1" type="success" size="mini" style="margin-left:6px">👁 Vision</el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="审核时间" :span="2">{{ task.endTime }}</el-descriptions-item>
          </el-descriptions>

          <!-- 整体建议(无违规时) -->
          <el-alert v-if="!results || results.length === 0"
                    type="success" :closable="false" show-icon
                    title="AI 审核未发现违规项"
                    style="margin-top:12px">
          </el-alert>

          <!-- 违规明细 -->
          <div v-else class="violations">
            <div class="violations-title">
              违规明细 ({{ failResults.length }} 项 FAIL · {{ warnResults.length }} 项 WARN)
            </div>
            <el-table :data="results" size="mini" border stripe>
              <el-table-column prop="itemCode" label="编号" width="90">
                <template slot-scope="{ row }">
                  <el-tag size="mini" type="info">{{ row.itemCode }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="status" label="级别" width="70" align="center">
                <template slot-scope="{ row }">
                  <el-tag size="mini" :type="row.status === 'WARN' ? 'warning' : 'danger'">
                    {{ row.status }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="feeType" label="费用" width="80">
                <template slot-scope="{ row }">{{ row.feeType || '通用' }}</template>
              </el-table-column>
              <el-table-column prop="docType" label="文档" width="100" show-overflow-tooltip>
                <template slot-scope="{ row }">{{ row.docType || '-' }}</template>
              </el-table-column>
              <el-table-column prop="reason" label="AI 判定原因" min-width="200" show-overflow-tooltip />
              <el-table-column prop="suggestion" label="处理建议" min-width="180" show-overflow-tooltip />
              <el-table-column label="实际值" width="160" show-overflow-tooltip>
                <template slot-scope="{ row }">
                  <span v-if="row.actualValues" style="font-family:monospace;font-size:11px;color:#606266">
                    {{ formatActualValues(row.actualValues) }}
                  </span>
                </template>
              </el-table-column>
            </el-table>
          </div>

          <!-- 友情提示 -->
          <div class="hint">
            <i class="el-icon-info"></i>
            AI 审核结果仅供参考,最终是否通过/驳回请由审核官员根据材料实际情况判定
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { getSpendingAiAudit } from '@/request/api.js'

const OVERALL_TAG = { PASS: 'success', WARN: 'warning', REJECT: 'danger' }
const OVERALL_LABEL = { PASS: '通过', WARN: '警告', REJECT: '驳回建议' }

export default {
  name: 'AiAuditPanel',
  props: {
    orderId: { type: [String, Number], required: true },
    defaultExpanded: { type: Boolean, default: true }
  },
  data() {
    return {
      loading: false,
      expanded: this.defaultExpanded,
      task: null,
      results: [],
      pollTimer: null
    }
  },
  computed: {
    overallTagType() {
      return OVERALL_TAG[this.task?.overallStatus] || 'info'
    },
    overallText() {
      return OVERALL_LABEL[this.task?.overallStatus] || this.task?.overallStatus || '-'
    },
    failResults() {
      return (this.results || []).filter(r => r.status === 'FAIL')
    },
    warnResults() {
      return (this.results || []).filter(r => r.status === 'WARN')
    },
    /** 从 task.llmConfigSnap 解析 LLM 信息 */
    llmInfo() {
      if (!this.task || !this.task.llmConfigSnap) return {}
      try {
        return JSON.parse(this.task.llmConfigSnap)
      } catch (e) { return {} }
    }
  },
  created() {
    this.reload()
  },
  beforeDestroy() {
    this.stopPolling()
  },
  watch: {
    orderId() {
      this.reload()
    }
  },
  methods: {
    async reload() {
      if (!this.orderId) return
      this.loading = true
      try {
        const res = await getSpendingAiAudit(this.orderId)
        if (res && res.code === 200) {
          this.task = res.data?.task || null
          this.results = res.data?.results || []

          // 如果还在跑,开启轮询
          if (this.task && (this.task.status === 'PENDING' || this.task.status === 'RUNNING')) {
            this.startPolling()
          } else {
            this.stopPolling()
          }
        } else {
          this.$message.error(res?.msg || '加载 AI 审核结果失败')
        }
      } catch (e) {
        this.$message.error('网络错误: ' + (e.message || e))
      } finally {
        this.loading = false
      }
    },

    startPolling() {
      this.stopPolling()
      this.pollTimer = setInterval(async () => {
        try {
          const res = await getSpendingAiAudit(this.orderId)
          if (res && res.code === 200) {
            this.task = res.data?.task || null
            this.results = res.data?.results || []
            if (this.task && this.task.status !== 'PENDING' && this.task.status !== 'RUNNING') {
              this.stopPolling()
            }
          }
        } catch (e) { /* 静默 */ }
      }, 5000)
    },

    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },

    formatCost(ms) {
      if (!ms) return '-'
      return (ms / 1000).toFixed(1) + ' 秒'
    },

    formatActualValues(json) {
      if (!json) return ''
      try {
        const obj = JSON.parse(json)
        return Object.entries(obj).map(([k, v]) => `${k}: ${v}`).join(' · ')
      } catch (e) {
        return json
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.ai-audit-panel {
  background: #fff;
  border-radius: 10px;
  margin-bottom: 16px;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #e6e9f0;
  overflow: hidden;

  .ai-header {
    padding: 14px 20px;
    background: linear-gradient(135deg, #f5f7fa, #ecf2fc);
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    user-select: none;
    transition: background 0.15s;

    &:hover {
      background: linear-gradient(135deg, #ecf2fc, #e0eaf8);
    }

    .ai-header-left {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .ai-icon {
      color: #667eea;
      font-size: 20px;
    }

    .ai-title {
      font-size: 15px;
      font-weight: 600;
      color: #303133;
    }

    .ai-summary {
      font-size: 13px;
      color: #606266;
      margin-left: 8px;
    }

    .ai-arrow {
      color: #909399;
      font-size: 14px;
      transition: transform 0.25s;

      &.rotated {
        transform: rotate(180deg);
      }
    }
  }

  .ai-body {
    padding: 16px 20px;
    border-top: 1px solid #ebeef5;

    .running-tip, .failed-tip {
      text-align: center;
      padding: 24px 0;
    }

    .running-tip {
      color: #606266;
    }

    .meta {
      margin-bottom: 12px;
    }

    .violations {
      margin-top: 12px;

      .violations-title {
        font-size: 13px;
        font-weight: 600;
        color: #303133;
        margin-bottom: 8px;
      }
    }

    .hint {
      margin-top: 12px;
      padding: 8px 12px;
      background: #f5f7fa;
      border-radius: 6px;
      font-size: 12px;
      color: #909399;

      i {
        margin-right: 4px;
      }
    }
  }
}
</style>
